import './AdListItems.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ErrorMessage from '../../../../common/ErrorMessage';
import ListToggle from '../../../../common/ListToggle';
import Loader from '../../../../common/Loader';
import type { AdListData, AdRotation, Rotation } from '../../../../common/SequenceWeight/types';
import bem, { withOptionalClassName } from '../../../../utils/bem';
import AdListItem from '../AdListItem';
import AdListItemInteractive from '../AdListItemInteractive/AdListItemInteractive';
import type { AdListOptionalFormState } from '../types';
import type { PreparedAdData } from './VirtualizedAdList';
import VirtualizedAdList from './VirtualizedAdList';

const [block, element] = bem('ad-list-items');

type AdListElementWithIndex = {
  adListElement: AdListOptionalFormState;
  adIndex: number;
};

export interface AdListItemsProps {
  adList: AdListOptionalFormState[];
  error?: string;
  loading?: boolean;
  selectedAdIndex?: number;
  adListData: AdListData;
  notInteractive?: boolean;
  className?: string;
  onClickDelete?: (adIndex: number) => void;
  onClickAd?: (adIndex: number, adId?: string) => void;
  scrollElementRef?: React.RefObject<HTMLElement>;
}

function AdListItems({
  adList,
  error,
  loading,
  selectedAdIndex,
  onClickAd,
  onClickDelete,
  adListData,
  notInteractive,
  className,
  scrollElementRef,
}: AdListItemsProps): JSX.Element {
  const [adListElementsWithIndex, setAdListElementsWithIndex] = useState<AdListElementWithIndex[]>([]);

  useEffect(() => {
    const adListElementsModified: AdListElementWithIndex[] = adList.map(
      (adListElement: AdListOptionalFormState, index: number) => {
        return { adListElement, adIndex: index };
      }
    );

    setAdListElementsWithIndex(adListElementsModified);
  }, [adList]);

  const selectedFocusRef = useRef<HTMLLIElement[]>([]);

  const handleClickDelete = useCallback(
    (adIndex: number): void => {
      if (!notInteractive && onClickDelete) {
        onClickDelete(adIndex);
      }

      const selectedAdFocused = selectedFocusRef.current[adIndex - 1];

      if (selectedAdFocused) {
        selectedAdFocused.focus();
      }
    },
    [notInteractive, onClickDelete]
  );

  const renderAdItem = useCallback(
    (
      adListElement: AdListOptionalFormState,
      adIndex: number,
      weight: number,
      isSequence: boolean,
      onContentChange?: () => void
    ): JSX.Element => {
      if (notInteractive) {
        return (
          <AdListItem
            adListElement={adListElement}
            isSaved={true}
            isSelected={false}
            weight={weight}
            key={adIndex}
            isSequence={isSequence}
            onContentChange={onContentChange}
          />
        );
      } else {
        return (
          <AdListItemInteractive
            selectedFocusRef={(el: HTMLLIElement): HTMLLIElement => (selectedFocusRef.current[adIndex] = el)}
            key={adIndex}
            adListElement={adListElement}
            adIndex={adIndex}
            isSelected={selectedAdIndex === adIndex}
            onClick={onClickAd as (adIndex: number, adId?: string) => void}
            onClickDelete={(): void => handleClickDelete(adIndex)}
            weight={weight}
            isAdListWithOneItem={adList.length === 1}
            isSequence={isSequence}
            onContentChange={onContentChange}
          />
        );
      }
    },
    [notInteractive, selectedAdIndex, onClickAd, adList.length, handleClickDelete]
  );

  // Links ad entries (from rotations, sequences, or standalone) to their corresponding ad list elements
  const linkAdEntriesWithAdListElements = useCallback(
    (adEntries: AdRotation[]): PreparedAdData[] => {
      return adEntries
        .map((adEntry: AdRotation) => {
          const matchingElement = adListElementsWithIndex.find(
            (element: AdListElementWithIndex) => element.adListElement.ad.id === adEntry.adId
          );
          return { adRotation: adEntry, matchedElement: matchingElement };
        })
        .filter((entry) => entry.matchedElement);
    },
    [adListElementsWithIndex]
  );

  // Prepare standalone ads data
  const standaloneAdsData = useMemo(() => linkAdEntriesWithAdListElements(adListData.adsWithoutWeightOrSequence), [
    adListData.adsWithoutWeightOrSequence,
    linkAdEntriesWithAdListElements,
  ]);

  const renderSequenceToggles = useCallback(
    () =>
      [...adListData.sequences].map((sequence) => {
        const sequenceAdsData = linkAdEntriesWithAdListElements(sequence.adsList);
        return (
          <div className={element('toggle-container')} key={sequence.id}>
            <ListToggle
              ignoreInitialExpanded
              appear={false}
              header={
                <div>
                  <span className={element('list-name')}>{sequence.name}</span>
                  <div className={element('list-type')}>Sequence</div>
                </div>
              }
            >
              <VirtualizedAdList
                adsData={sequenceAdsData}
                isSequence={true}
                scrollElement={scrollElementRef?.current}
                renderAdItem={renderAdItem}
              />
            </ListToggle>
          </div>
        );
      }),
    [adListData.sequences, linkAdEntriesWithAdListElements, scrollElementRef, renderAdItem]
  );

  const scrollElement = scrollElementRef?.current;

  if (loading) return <Loader />;
  // display error if there's no ad, or only a default ad
  if (error && (adList.length === 0 || (adList.length === 1 && adList[0].ad.id === '')))
    return <ErrorMessage message={error} />;

  if (adList.length === 0 && notInteractive) return <div className={element('no-ads-message')}>No ads.</div>;

  if (adList.length === 0) return <ErrorMessage message="No ads. Please create an ad!" />;

  // When using WindowScroller (scrollElement provided), we need different styling
  const blockClassName = scrollElement ? block('window-scroller') : block();

  const hasSequences = adListData.sequences.length > 0;

  return (
    <>
      <ul className={withOptionalClassName(blockClassName, className)} data-testid="ad-list-items">
        {adList.map((adListElement: AdListOptionalFormState, adIndex: number) =>
          !adListElement.ad.id ? renderAdItem(adListElement, adIndex, 0, false) : null
        )}
        {[...adListData.rotations].map((rotation: Rotation) => {
          const rotationAdsData = linkAdEntriesWithAdListElements(rotation.adsList);
          return (
            <div className={element('toggle-container')} key={rotation.id}>
              <ListToggle
                ignoreInitialExpanded
                appear={false}
                header={
                  <div>
                    <span className={element('list-name')}>{rotation.name}</span>
                    <div className={element('list-type')}>Rotation</div>
                  </div>
                }
              >
                <VirtualizedAdList
                  adsData={rotationAdsData}
                  isSequence={false}
                  scrollElement={scrollElement}
                  renderAdItem={renderAdItem}
                />
              </ListToggle>
            </div>
          );
        })}
        {hasSequences ? (
          <>
            <div className={element('toggle-container')}>
              <ListToggle
                ignoreInitialExpanded
                appear={false}
                header={
                  <div className={element('group-header')}>
                    <span className={element('list-name')}>Sequenced</span>
                    <span className={element('group-count')}>
                      {adListData.sequences.reduce((count, seq) => count + seq.adsList.length, 0)}
                    </span>
                  </div>
                }
              >
                {renderSequenceToggles()}
              </ListToggle>
            </div>
            <div className={element('toggle-container')}>
              <ListToggle
                ignoreInitialExpanded
                appear={false}
                header={
                  <div className={element('group-header')}>
                    <span className={element('list-name')}>Unsequenced</span>
                    <span className={element('group-count')}>{standaloneAdsData.length}</span>
                  </div>
                }
              >
                <VirtualizedAdList
                  adsData={standaloneAdsData}
                  isSequence={false}
                  scrollElement={scrollElement}
                  renderAdItem={renderAdItem}
                />
              </ListToggle>
            </div>
          </>
        ) : (
          <>
            {renderSequenceToggles()}
            <VirtualizedAdList
              adsData={standaloneAdsData}
              isSequence={false}
              scrollElement={scrollElement}
              renderAdItem={renderAdItem}
            />
          </>
        )}
      </ul>
      {/* {error && !adList && <ErrorMessage message={error} />} */}
    </>
  );
}

export default AdListItems;
